import log from 'loglevel';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header, SecondaryButton, SocialLoginButtons, SubHeader, Text } from '../../../components';
import { LastIdentifier } from '../../../components/authentication/login-init/LastIdentifier';
import { LoginForm } from '../../../components/authentication/login-init/LoginForm';
import { useTelemetry } from '../../../hooks/useTelemetry';
export const LoginInit = ({ block, initialAutoFocus }) => {
    const { t } = useTranslation('translation', { keyPrefix: `login.login-init.login-init` });
    const { logMethodCalled } = useTelemetry();
    const [loading, setLoading] = useState(false);
    const [socialLoadingInProgress, setSocialLoadingInProgress] = useState(undefined);
    const [showLastIdentifier, setShowLastIdentifier] = useState(!!block.data.lastIdentifier);
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const [onComponentClick, setOnComponentClick] = useState(() => { });
    useEffect(() => {
        setLoading(false);
        if (block.data.socialData.finished && !block.error) {
            const socialAbort = new AbortController();
            void block.finishSocialVerify(socialAbort).finally(() => setSocialLoadingInProgress(false));
            setSocialLoadingInProgress(true);
            return () => {
                socialAbort.abort();
            };
        }
        else {
            setSocialLoadingInProgress(false);
        }
        if (block.data.loginIdentifierError) {
            setShowLastIdentifier(false);
        }
        return void 0;
    }, [block]);
    // we have to be very careful with conditionalUI as WebKit based browser historically had a number of "restrictions" on how we can start it
    useEffect(() => {
        void block.startConditionalUIOnPageLoad().then(result => {
            if (result) {
                logMethodCalled('conditionalUIOnPageLoad', 'loginInit');
                log.debug('CUI: page load');
                void block
                    .continueWithConditionalUI({ onAuthenticatorCompleted: () => setLoading(true) })
                    .catch(() => setLoading(false));
            }
        });
        void block.startConditionalUIOnFirstUserInteraction().then(result => {
            if (result) {
                log.debug('CUI: first click');
                setOnComponentClick(() => {
                    return () => {
                        logMethodCalled('conditionalUIOnFirstUserInteraction', 'loginInit');
                        log.debug('calling continueWithConditionalUI');
                        void block
                            .continueWithConditionalUI({ onAuthenticatorCompleted: () => setLoading(true) })
                            .catch(() => setLoading(false));
                    };
                });
            }
        });
    }, []);
    const headerText = useMemo(() => t('header'), [t]);
    const subheaderText = useMemo(() => t('subheader'), [t]);
    const signUpText = useMemo(() => t('text_signup'), [t]);
    const flowChangeButtonText = useMemo(() => t('button_signup'), [t]);
    const textDivider = useMemo(() => t('text_divider'), [t]);
    const startSocialLogin = (providerType) => {
        setSocialLoadingInProgress(true);
        logMethodCalled('startSocialLogin', 'loginInit');
        void block.startSocialVerify(providerType);
    };
    // user explicitly discards the last identifier offered to him => we will clear if from localstorage
    const discardOfferedLastIdentifier = () => {
        logMethodCalled('discardOfferedLastIdentifier', 'loginInit');
        block.discardOfferedLastIdentifier();
        setShowLastIdentifier(false);
    };
    return (React.createElement("div", { onClick: onComponentClick, className: 'cb-login-init' },
        React.createElement(Header, { size: 'lg' }, headerText),
        React.createElement(SubHeader, null,
            subheaderText,
            block.common.appName),
        showLastIdentifier ? (React.createElement(LastIdentifier, { block: block, socialLoadingInProgress: socialLoadingInProgress, loading: loading, setLoading: setLoading, switchToLoginForm: discardOfferedLastIdentifier })) : (React.createElement(LoginForm, { block: block, loading: loading, socialLoadingInProgress: socialLoadingInProgress, setLoading: setLoading, initialAutoFocus: initialAutoFocus })),
        React.createElement(SocialLoginButtons, { dividerText: textDivider, socialLogins: block.data.socialData.providers, t: t, socialLoadingInProgress: socialLoadingInProgress, onClick: startSocialLogin }),
        block.isSignupEnabled() && (React.createElement(Text, { level: '2', fontWeight: 'normal', textColorVariant: 'script', className: 'cb-auth-change-section' },
            signUpText,
            React.createElement(SecondaryButton, { colorVariant: 'link', disabled: loading, onClick: () => {
                    logMethodCalled('switchToSignup', 'loginInit');
                    block.switchToSignup();
                } }, flowChangeButtonText)))));
};
